/**
 * Mailshot Domain Model
 * 
 * Encapsulates business logic and validation rules for mailshots.
 * Pure business logic - no Firestore operations.
 */

// Where a property came from is ONE rule (propertySource.js). The ".js" is needed: the
// functions load this file as Node loads a module, which does not guess an extension.
import { countBySource } from './propertySource.js';

export class Mailshot {
  constructor(data = {}) {
    // Preserve every field from the source document so reads are loss-free —
    // components can rely on raw fields that aren't explicitly modeled below.
    // Writes are unaffected: toFirestore() only serialises the named fields.
    Object.assign(this, data);

    // Core identification
    this.id = data.id || null;
    this.name = data.name || null;
    this.description = data.description || null;
    
    // Campaign relationship
    this.campaignId = data.campaignId || null;
    
    // Property relationships
    this.propertyIds = data.propertyIds || [];
    this.scrapeIds = data.scrapeIds || [];
    
    // Statistics (cached)
    this.stats = data.stats || {
      total: 0,
      inMailshots: 0,
      forSale: 0,
      sstc: 0,
    };
    
    // Detailed cached counts (for UI)
    this.cachedCounts = data.cachedCounts || {
      total: 0,
      byScrape: {},
      byStatus: {},
      manuallyAdded: 0,
      testProperties: 0,
    };
    
    // Status tracking
    this.status = data.status || 'draft'; // draft, sent, completed
    this.deliveredCount = data.deliveredCount || 0;
    this.sentCount = data.sentCount || 0;
    
    // Timestamps
    this.createdAt = data.createdAt || null;
    this.updatedAt = data.updatedAt || null;
    this.sentAt = data.sentAt || null;

    // Promoters data (calculated via calculateMailshotPromoters cloud function)
    this.promotersInArea = data.promotersInArea || [];
    this.promotersCalculatedAt = data.promotersCalculatedAt || null;
    this.promotersSearchCenters = data.promotersSearchCenters || [];
    this.promotersGeocodingWarnings = data.promotersGeocodingWarnings || [];
    this.promotersCatchmentBuffer = data.promotersCatchmentBuffer || 0.5;
    this.promotersScrapeIdsSnapshot = data.promotersScrapeIdsSnapshot || []; // Scrape IDs at calculation time
    this.promoterPropertyMatches = data.promoterPropertyMatches || {}; // Property ID -> match details
  }

  /**
   * Calculate statistics from a list of properties
   * @param {Property[]} properties - Array of Property instances
   * @returns {Object} - Statistics object
   */
  calculateStats(properties) {
    const stats = {
      total: properties.length,
      inMailshots: 0,
      forSale: 0,
      sstc: 0,
      underOffer: 0,
      offMarket: 0,
      delivered: 0,
      sent: 0,
    };
    
    const statusCountsMap = {};
    let testProperties = 0;
    
    properties.forEach(property => {
      // Count by status
      if (property.propertyStatus === 'For Sale') {
        stats.forSale++;
      } else if (property.propertyStatus === 'Sold STC') {
        stats.sstc++;
      } else if (property.propertyStatus === 'Under Offer') {
        stats.underOffer++;
      } else if (property.propertyStatus === 'Off Market') {
        stats.offMarket++;
      }
      
      // Count if in mailshots (any mailshot, not just this one)
      if (property.mailshotIds && property.mailshotIds.length > 0) {
        stats.inMailshots++;
      }
      
      // Count delivery status
      if (property.deliveryStatus === 'delivered') {
        stats.delivered++;
      } else if (property.deliveryStatus === 'sent') {
        stats.sent++;
      }
      
      
      // Count test properties
      if (property.isTestProperty) {
        testProperties++;
      }
      
      // Count by status for cache
      const status = property.propertyStatus || 'Unknown';
      statusCountsMap[status] = (statusCountsMap[status] || 0) + 1;
    });
    
    // By source: a scrape, an import, or neither ("manually added"). ONE rule, the same one
    // the running counts use (propertySource.js), so a recount cannot disagree with them.
    const bySource = countBySource(properties);

    // Build cached counts structure
    const cachedCounts = {
      total: properties.length,
      byScrape: bySource.byScrape,
      byStatus: statusCountsMap,
      manuallyAdded: bySource.manuallyAdded,
      testProperties,
    };
    // Present only when some property came from an import: a mailshot with none stores what it always did.
    if (bySource.byImport) cachedCounts.byImport = bySource.byImport;
    
    return { stats, cachedCounts };
  }

  /**
   * Check if this mailshot can be safely deleted
   * @returns {boolean} - True if mailshot can be deleted
   */
  canBeDeleted() {
    // Cannot delete if it has properties
    if (this.propertyIds && this.propertyIds.length > 0) {
      return false;
    }
    
    return true;
  }

  /**
   * Get reason why mailshot cannot be deleted
   * @returns {string|null} - Reason or null if can be deleted
   */
  getDeleteBlockReason() {
    if (this.propertyIds && this.propertyIds.length > 0) {
      return `Mailshot contains ${this.propertyIds.length} property/properties`;
    }
    
    return null;
  }

  /**
   * Get list of collections this mailshot affects
   * Used for cascade operations
   * @returns {string[]} - Array of collection names
   */
  getAffectedCollections() {
    const affected = ['discovery_mailshots'];
    
    if (this.propertyIds && this.propertyIds.length > 0) {
      affected.push('discovery_properties');
    }
    
    if (this.campaignId) {
      affected.push('discovery_campaigns');
    }
    
    return affected;
  }

  /**
   * Check if a property is in this mailshot
   * @param {string} propertyId - Property ID to check
   * @returns {boolean} - True if property is in this mailshot
   */
  hasProperty(propertyId) {
    return this.propertyIds && this.propertyIds.includes(propertyId);
  }

  /**
   * Check if a scrape is in this mailshot
   * @param {string} scrapeId - Scrape ID to check
   * @returns {boolean} - True if scrape is in this mailshot
   */
  hasScrape(scrapeId) {
    return this.scrapeIds && this.scrapeIds.includes(scrapeId);
  }

  /**
   * Get count of properties from a specific scrape
   * @param {string} scrapeId - Scrape ID
   * @returns {number} - Count of properties
   */
  getPropertyCountByScrape(scrapeId) {
    if (!this.cachedCounts || !this.cachedCounts.byScrape) {
      return 0;
    }
    return this.cachedCounts.byScrape[scrapeId] || 0;
  }

  /**
   * Get count of properties by status
   * @param {string} status - Property status
   * @returns {number} - Count of properties
   */
  getPropertyCountByStatus(status) {
    if (!this.cachedCounts || !this.cachedCounts.byStatus) {
      return 0;
    }
    return this.cachedCounts.byStatus[status] || 0;
  }

  /**
   * Check if promoters have been calculated for this mailshot
   * @returns {boolean} - True if promoters data exists
   */
  hasPromotersData() {
    return this.promotersCalculatedAt !== null;
  }

  /**
   * Check if promoters data is stale (scrapes changed since calculation)
   * @returns {boolean} - True if promoters should be recalculated
   */
  hasStalePromotersData() {
    if (!this.promotersCalculatedAt) return false; // Not calculated yet, not stale
    if (!this.promotersScrapeIdsSnapshot) return false; // No snapshot, can't determine staleness

    // Compare scrapeIds arrays - data is stale if scrapes have changed
    const currentScrapeIds = [...(this.scrapeIds || [])].sort();
    const snapshotScrapeIds = [...(this.promotersScrapeIdsSnapshot || [])].sort();

    // Check if arrays are different
    if (currentScrapeIds.length !== snapshotScrapeIds.length) return true;
    
    for (let i = 0; i < currentScrapeIds.length; i++) {
      if (currentScrapeIds[i] !== snapshotScrapeIds[i]) return true;
    }

    return false; // Scrapes haven't changed
  }

  /**
   * Get promoters filtered by type
   * @param {string} filter - 'all' | 'target' | 'active'
   * @returns {Array} - Filtered promoters array
   */
  getPromotersByFilter(filter) {
    if (!this.promotersInArea) return [];

    switch (filter) {
      case 'target':
        return this.promotersInArea.filter(p => !p.active);
      case 'active':
        return this.promotersInArea.filter(p => p.active);
      default:
        return this.promotersInArea;
    }
  }

  /**
   * Get promoter counts by type
   * @returns {Object} - { total, target, active }
   */
  getPromoterCounts() {
    const promoters = this.promotersInArea || [];
    return {
      total: promoters.length,
      target: promoters.filter(p => !p.active).length,
      active: promoters.filter(p => p.active).length
    };
  }

  /**
   * Validate mailshot data
   * @throws {Error} - If validation fails
   * @returns {boolean} - True if valid
   */
  validate() {
    if (!this.name || this.name.trim() === '') {
      throw new Error('name is required');
    }
    
    if (this.name.length > 100) {
      throw new Error('name must be 100 characters or less');
    }
    
    // Validate propertyIds is array
    if (this.propertyIds && !Array.isArray(this.propertyIds)) {
      throw new Error('propertyIds must be an array');
    }
    
    // Validate scrapeIds is array
    if (this.scrapeIds && !Array.isArray(this.scrapeIds)) {
      throw new Error('scrapeIds must be an array');
    }
    
    // Validate status
    const validStatuses = ['draft', 'sent', 'completed'];
    if (this.status && !validStatuses.includes(this.status)) {
      throw new Error(`Invalid status: ${this.status}. Must be one of: ${validStatuses.join(', ')}`);
    }
    
    return true;
  }

  /**
   * Serialize to Firestore document
   * @returns {Object} - Firestore document data
   */
  toFirestore() {
    const doc = {
      name: this.name,
      propertyIds: this.propertyIds,
      scrapeIds: this.scrapeIds,
      stats: this.stats,
      cachedCounts: this.cachedCounts,
      status: this.status,
      deliveredCount: this.deliveredCount,
      sentCount: this.sentCount,
    };
    
    // Optional fields
    if (this.description) doc.description = this.description;
    if (this.campaignId) doc.campaignId = this.campaignId;
    if (this.createdAt) doc.createdAt = this.createdAt;
    if (this.updatedAt) doc.updatedAt = this.updatedAt;
    if (this.sentAt) doc.sentAt = this.sentAt;

    // Promoters data (if calculated)
    if (this.promotersInArea) doc.promotersInArea = this.promotersInArea;
    if (this.promotersCalculatedAt) doc.promotersCalculatedAt = this.promotersCalculatedAt;
    if (this.promotersSearchCenters) doc.promotersSearchCenters = this.promotersSearchCenters;
    if (this.promotersGeocodingWarnings) doc.promotersGeocodingWarnings = this.promotersGeocodingWarnings;
    if (this.promotersCatchmentBuffer) doc.promotersCatchmentBuffer = this.promotersCatchmentBuffer;
    if (this.promotersScrapeIdsSnapshot) doc.promotersScrapeIdsSnapshot = this.promotersScrapeIdsSnapshot;
    if (this.promoterPropertyMatches) doc.promoterPropertyMatches = this.promoterPropertyMatches;

    return doc;
  }

  /**
   * Deserialize from Firestore document
   * @param {Object} doc - Firestore document snapshot
   * @returns {Mailshot} - Mailshot instance
   */
  static fromFirestore(doc) {
    if (!doc) return null;
    
    const data = doc.data ? doc.data() : doc;
    const id = doc.id || data.id;
    
    return new Mailshot({
      id,
      ...data
    });
  }

  /**
   * Create a plain object for display (removes internal fields)
   * @returns {Object} - Plain object suitable for JSON serialization
   */
  toJSON() {
    return {
      id: this.id,
      name: this.name,
      description: this.description,
      campaignId: this.campaignId,
      propertyCount: this.propertyIds?.length || 0,
      stats: this.stats,
      cachedCounts: this.cachedCounts,
      status: this.status,
      createdAt: this.createdAt,
    };
  }
}
